import React, { useState } from "react";
import * as RadixSelect from "@radix-ui/react-select";
import { DownIcon } from "assets/icons";
import styled, { css } from "styled-components";
import useMediaView from "features/common/hooks/useMediaView";
import BottomSheet from "../BottomSheet/BottomSheet";

export interface SelectProps extends RadixSelect.SelectProps {
  list: {
    label: string;
    value: string | null;
    // (만두피) 권한을 처리하기 위해 null 설정 일반 회원일 경우 null로 처리가 되어야함
  }[];
  style?: React.CSSProperties;
  placeholder?: string;
  className?: string;
  errorMessage?: string;
  listViewCount?: number;
  isCalender?: boolean;
  onClick?: () => void;
}

const Select = ({
  list,
  children,
  placeholder,
  style,
  className,
  listViewCount,
  errorMessage,
  isCalender,
  onClick,
  ...args
}: SelectProps) => {
  const isSmall = useMediaView("small");
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {(!isSmall || (isSmall && isCalender)) && (
        <SelectRoot {...args}>
          <RadixSelect.Trigger asChild>
            <SelectTrigger
              className={className}
              style={style}
              error={!!errorMessage}
              onClick={onClick}
            >
              <RadixSelect.Value placeholder={placeholder} />
              <DownIcon />
            </SelectTrigger>
          </RadixSelect.Trigger>

          {errorMessage && <LabelStyle>{errorMessage}</LabelStyle>}

          <RadixSelect.Portal>
            <SelectContent
              position="popper"
              sideOffset={-4}
              style={{
                zIndex: 99999999,
                ...(listViewCount
                  ? { maxHeight: `${listViewCount * 5}rem` }
                  : {}),
              }}
            >
              <RadixSelect.Viewport>
                {list.map((item) => (
                  <RadixSelect.Item
                    value={item.value as string}
                    // (만두피) 권한을 처리하기 위해 null 설정 일반 회원일 경우 null로 처리가 되어야함
                    key={item.value}
                    className="select__item"
                  >
                    <RadixSelect.ItemText>{item.label}</RadixSelect.ItemText>
                  </RadixSelect.Item>
                ))}
              </RadixSelect.Viewport>
            </SelectContent>
          </RadixSelect.Portal>
        </SelectRoot>
      )}
      {isSmall && !isCalender && (
        <>
          <SelectTrigger
            className={className}
            style={style}
            error={!!errorMessage}
            onClick={() => setIsOpen(true)}
          >
            <input
              disabled
              placeholder={placeholder}
              style={{
                border: "none",
                outline: "none",
                background: "none",
                pointerEvents: "none",
                display: "inline-block",
                width: "100%",
              }}
              value={
                list.find((item) => item.value === args.value)?.label ?? ""
              }
            />
            <DownIcon />
          </SelectTrigger>

          {errorMessage && <LabelStyle>{errorMessage}</LabelStyle>}

          {isOpen && (
            <BottomSheet
              title={placeholder}
              isOpen={isOpen}
              setOpen={setIsOpen}
            >
              {list.map((item) => (
                <div
                  key={item.value}
                  onClick={() => {
                    args.onValueChange?.(item.value as string);
                    // (만두피) 권한을 처리하기 위해 null 설정 일반 회원일 경우 null로 처리가 되어야함
                    setIsOpen(false);
                  }}
                >
                  {item.label}
                </div>
              ))}
            </BottomSheet>
          )}
        </>
      )}
    </>
  );
};

export default Select;

const SelectTrigger = styled.div<{ error: boolean; readOnly?: boolean }>`
  ${({ readOnly }) =>
    readOnly &&
    css`
      background-color: ${({ theme }) => theme.color.grey[100]};
    `}

  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 0.5rem;

  ${({ theme }) => theme.typography.body1.regular};

  padding: 1.1rem;
  border: 1px solid
    ${({ theme, error }) =>
      error ? theme.color.text.danger : theme.color.stroke.light};
  background-color: ${({ theme }) => theme.color.background.white};

  svg {
    color: ${({ theme }) => theme.color.grey[600]};
  }

  :hover {
    border: 1px solid ${({ theme }) => theme.color.stroke.dark};
  }

  &[data-placeholder] {
    color: ${({ theme }) => theme.color.text.placeholder};
  }

  &[data-disabled] {
    background-color: ${({ theme }) => theme.color.grey[100]};
    color: ${({ theme }) => theme.color.text.disabled};

    :hover {
      border: 1px solid ${({ theme }) => theme.color.stroke.light};
    }

    svg {
      opacity: 0.5;
    }
  }

  ${({ theme }) => css`
    ${theme.media.small} {
      padding: 1.1rem 1.4rem;
      ${({ theme }) => theme.typography.caption1.regular};
      svg {
        width: 2rem;
        height: 2rem;
      }
    }
  `};
`;

const SelectContent = styled(RadixSelect.Content)`
  border-radius: 0px 0px 5px 5px;
  background-color: ${({ theme }) => theme.color.background.white};
  border: 1px solid ${({ theme }) => theme.color.stroke.light};
  color: ${({ theme }) => theme.color.text.secondary};
  ${({ theme }) => theme.typography.body1.regular};

  width: var(--radix-select-trigger-width);
  max-height: var(--radix-select-content-available-height);

  .select__item {
    padding: 1.2rem 1.4rem;
    outline: none;
    cursor: pointer;

    :hover {
      background-color: ${({ theme }) => theme.color.background.default};
    }

    &[data-state="checked"] {
      color: ${({ theme }) => theme.color.text.highEmphasis};
      background-color: ${({ theme }) => theme.color.background.default};
    }
  }

  ${({ theme }) => css`
    ${theme.media.small} {
      ${({ theme }) => theme.typography.caption1.regular};
      .select__item {
        padding: 1rem 1.4rem;
      }
    }
  `};
`;

const SelectRoot = styled(RadixSelect.Root)`
  display: flex;
  flex-direction: column;
`;

const LabelStyle = styled.label`
  padding-left: 1.6rem;
  font-weight: 400;
  font-size: 1.4rem;
  line-height: 3.2rem;
  ${({ theme }) => theme.typography.body3.regular};
  color: ${({ theme }) => theme.color.text.danger};
`;
